import {
  LegacyPaginationFields,
  NormalizedPaginatedResponseWithLegacy,
  PageMetadata,
} from '@models/pagination.model';

type UnknownRecord = Record<string, unknown>;

interface NestedPaginatedResponse<T = unknown> {
  content: T[];
  page: unknown;
}

function isObject(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

function hasArrayContent(
  value: unknown
): value is UnknownRecord & { content: unknown[] } {
  return isObject(value) && Array.isArray(value['content']);
}

function toPageMetadataFromNestedPage(value: unknown): PageMetadata | null {
  if (!isObject(value)) {
    return null;
  }

  const totalItems = isNumber(value['totalItems'])
    ? value['totalItems']
    : isNumber(value['totalElements'])
      ? value['totalElements']
      : null;
  const totalPages = isNumber(value['totalPages']) ? value['totalPages'] : null;
  const pageSize = isNumber(value['pageSize'])
    ? value['pageSize']
    : isNumber(value['size'])
      ? value['size']
      : null;
  const currentPage = isNumber(value['currentPage'])
    ? value['currentPage']
    : isNumber(value['number'])
      ? value['number']
      : null;

  if (
    totalItems === null ||
    totalPages === null ||
    pageSize === null ||
    currentPage === null
  ) {
    return null;
  }

  return {
    totalItems,
    totalPages,
    pageSize,
    currentPage,
  };
}

function toLegacyPaginationFields(page: PageMetadata): LegacyPaginationFields {
  return {
    totalElements: page.totalItems,
    totalPages: page.totalPages,
    size: page.pageSize,
    number: page.currentPage,
  };
}

function toLegacyPaginationFieldsFromRecord(
  value: UnknownRecord
): LegacyPaginationFields | null {
  const totalElements = isNumber(value['totalElements'])
    ? value['totalElements']
    : null;
  const totalPages = isNumber(value['totalPages'])
    ? value['totalPages']
    : null;
  const size = isNumber(value['size']) ? value['size'] : null;
  const number = isNumber(value['number']) ? value['number'] : null;

  if (
    totalElements === null ||
    totalPages === null ||
    size === null ||
    number === null
  ) {
    return null;
  }

  return {
    totalElements,
    totalPages,
    size,
    number,
  };
}

function isNestedPaginatedResponse(
  value: unknown
): value is NestedPaginatedResponse {
  if (!isObject(value)) {
    return false;
  }

  return hasArrayContent(value) && !!toPageMetadataFromNestedPage(value['page']);
}

function isFlatPaginatedResponse(value: unknown): value is UnknownRecord & {
  content: unknown[];
} {
  return hasArrayContent(value) && !!toLegacyPaginationFieldsFromRecord(value);
}

function buildNormalizedResponse<T>(
  content: T[],
  page: LegacyPaginationFields
): NormalizedPaginatedResponseWithLegacy<T> {
  return {
    content,
    page,
    ...page,
  };
}

export function normalizePaginatedResponse<T = unknown>(
  payload: unknown
): NormalizedPaginatedResponseWithLegacy<T> | null {
  if (!isObject(payload)) {
    return null;
  }

  if (isNestedPaginatedResponse(payload)) {
    const pageMetadata = toPageMetadataFromNestedPage(payload.page);

    if (!pageMetadata) {
      return null;
    }

    const page = toLegacyPaginationFields(pageMetadata);
    return buildNormalizedResponse(payload.content as T[], page);
  }

  if (isFlatPaginatedResponse(payload)) {
    const page = toLegacyPaginationFieldsFromRecord(payload);

    if (!page) {
      return null;
    }

    return buildNormalizedResponse(payload.content as T[], page);
  }

  return null;
}
